import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { API_BASE_URL } from "../config";
import { useFullScreenDialog } from "../useFullScreenDialog";

const ITEMS_API = `${API_BASE_URL}/inventory/items`;

// Copies are spelled out one by one rather than generated blindly, because what makes
// them distinct is the only thing telling two otherwise identical containers apart. The
// pattern below is a starting point the user can overwrite per copy.
const MAX_COPIES = 40;

// Which field carries the numbering. Either can: "First aid bag 1..14" in the details,
// or one kind of bag with no details at all and serial numbers 1..14. Whichever isn't
// chosen is left as it is on the source — so numbering by serial keeps the details the
// copies share, blank or not.
const FIELD_DETAILS = "details";
const FIELD_SERIAL = "serial_number";
const FIELD_LABEL = { [FIELD_DETAILS]: "Details", [FIELD_SERIAL]: "Serial number" };

// "First aid bag 3" -> { base: "First aid bag", next: 4 }, "AB-1" -> { base: "AB-",
// next: 2 }. Continuing a sequence is what people almost always want, so it's worth
// detecting; the base keeps any trailing separator so a serial doesn't gain a space.
export const numbering = (value) => {
  const trimmed = (value || "").trim();
  const match = trimmed.match(/^(.*?)(\d+)$/);
  if (match) return { base: match[1].replace(/\s+$/, ""), next: Number(match[2]) + 1 };
  return { base: trimmed, next: 2 };
};

// "First aid bag" + 2 -> "First aid bag 2"; "AB-" + 2 -> "AB-2"; "" + 2 -> "2".
const joinNumber = (base, n) =>
  base === "" ? String(n) : /[-_/.:#]$/.test(base) ? `${base}${n}` : `${base} ${n}`;

const norm = (v) => (v || "").trim().toLowerCase();

// `sourceLabel` is passed as a string rather than derived from a helper prop: the
// helpers are rebuilt on every parent render, so depending on one here would reset
// the form under the user's fingers.
export default function DuplicateContainerDialog({
  open, source, sourceLabel, items, kitName, onClose, onDone,
}) {
  const fullScreen = useFullScreenDialog();
  const sourceDetails = source?.details || "";
  const sourceSerial = source?.serial_number || "";
  // The kit is named after the container with its number dropped, whichever field that
  // number lives in — so the label is the right thing to read it from.
  const kitSeed = useMemo(() => numbering(sourceLabel), [sourceLabel]);
  // Number by whatever the source is already numbered by. A serial number on a
  // container is there to tell it from its siblings, so it wins when there is one.
  const defaultField = sourceSerial ? FIELD_SERIAL : FIELD_DETAILS;

  const [field, setField] = useState(defaultField);
  const [count, setCount] = useState(1);
  const [base, setBase] = useState("");
  const [start, setStart] = useState(2);
  const [values, setValues] = useState([]);
  const [edited, setEdited] = useState({}); // indexes the user has typed into
  const [link, setLink] = useState(true);
  const [kit, setKit] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCount(1);
    setLink(true);
    setKit(kitSeed.base);
    setField(defaultField);
  }, [open, kitSeed, defaultField]);

  // Seed the sequence from whichever field is doing the distinguishing — including
  // when the user switches it, since the two fields number differently.
  useEffect(() => {
    const seed = numbering(field === FIELD_SERIAL ? sourceSerial : sourceDetails);
    setBase(seed.base);
    setStart(seed.next);
    setEdited({});
  }, [field, sourceSerial, sourceDetails]);

  // Generated values, except where the user has overridden one.
  useEffect(() => {
    setValues(prev => Array.from({ length: count }, (_, n) =>
      edited[n] ? prev[n] : joinNumber(base, Number(start) + n)));
  }, [count, base, start, edited]);

  const alreadyInKit = Boolean(source?.kit_id);

  // What a copy will actually be: the numbered field takes the value, the other keeps
  // the source's — except the serial number, which a copy only has if it is what's
  // being numbered (otherwise it would name two objects the same thing).
  const identityOf = (value) => (field === FIELD_SERIAL
    ? [norm(sourceDetails), norm(value)]
    : [norm(value), ""]).join(" ");

  // Anything already sitting beside the source, so a copy can't be made that's
  // indistinguishable from it — the source itself included. Mirrors identityKey() in
  // pages/InventoryItems.jsx, narrowed to the rows a copy could collide with.
  const taken = useMemo(() => {
    if (!source) return new Set();
    return new Set(items
      .filter(i => i.type_id === source.type_id
                && (i.location_id ?? null) === (source.location_id ?? null)
                && norm(i.size) === norm(source.size))
      .map(i => [norm(i.details), norm(i.serial_number)].join(" ")));
  }, [items, source]);

  const valueError = (value, index) => {
    const key = identityOf(value);
    if (taken.has(key)) {
      return norm(value) === ""
        ? `Needed — without a ${FIELD_LABEL[field].toLowerCase()} this copy is the same as the original`
        : "Already used here";
    }
    if (values.findIndex((v, i) => i !== index && identityOf(v) === key) !== -1) return "Repeated";
    return null;
  };

  const errors = values.map(valueError);
  const canSave = values.length > 0 && errors.every(e => e === null)
    && (!link || alreadyInKit || kit.trim() !== "");

  const save = () => {
    setSaving(true);
    const copies = values.map(v => field === FIELD_SERIAL
      ? { serial_number: v.trim(), details: null }   // null details: same as the source
      : { details: v.trim(), serial_number: null });
    axios.post(`${ITEMS_API}/${source.id}/duplicate`,
               { copies, link, kit_name: link && !alreadyInKit ? kit.trim() : null })
      .then(res => onDone(res.data, link))
      .finally(() => setSaving(false));
  };

  const contents = source ? items.filter(i => i.location_id === source.id).length : 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle>Duplicate {sourceLabel || "container"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Each copy gets the same {contents === 1 ? "item" : "items"} inside
            ({contents}), nested pockets and all, with the same required quantities and
            the same counts — so a copy of a packed container reads as packed. Serial
            numbers, expiry dates and last-checked dates aren't copied, since those
            belong to the physical object. Drugs and Medical items are stocked in packs,
            which aren't copied either, so those start at nothing and show as short
            until someone enters the packs actually in there.
          </Typography>

          {/* Which field tells the copies apart. */}
          <Stack direction="row" spacing={2} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
            <Typography variant="body2">Number them by</Typography>
            <ToggleButtonGroup size="small" exclusive value={field}
                               onChange={(_, v) => v && setField(v)}>
              <ToggleButton value={FIELD_DETAILS}>{FIELD_LABEL[FIELD_DETAILS]}</ToggleButton>
              <ToggleButton value={FIELD_SERIAL}>{FIELD_LABEL[FIELD_SERIAL]}</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
            {field === FIELD_SERIAL
              ? sourceDetails
                ? `Every copy keeps the details “${sourceDetails}” and gets its own serial number.`
                : "Every copy is the same type with no details, told apart by its serial number."
              : "Every copy gets its own details. None of them gets a serial number — that belongs to the physical object, so it's filled in per container."}
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField label="How many copies" type="number" size="small" sx={{ width: 150 }}
                       value={count}
                       onChange={e => setCount(Math.max(1, Math.min(MAX_COPIES,
                                                                    Number(e.target.value) || 1)))} />
            <TextField label={`${FIELD_LABEL[field]} prefix`} size="small" sx={{ flex: 1 }}
                       value={base}
                       helperText="Each copy is this plus a number — leave it blank for just the number"
                       onChange={e => setBase(e.target.value)} />
            <TextField label="Numbered from" type="number" size="small" sx={{ width: 140 }}
                       value={start} onChange={e => setStart(e.target.value)} />
          </Stack>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {FIELD_LABEL[field]} of each copy
            </Typography>
            <Stack spacing={1}>
              {values.map((value, index) => (
                <TextField key={index} size="small" fullWidth value={value}
                           error={Boolean(errors[index])} helperText={errors[index] || undefined}
                           onChange={e => {
                             setEdited(m => ({ ...m, [index]: true }));
                             setValues(list => list.map((v, i) => (i === index ? e.target.value : v)));
                           }} />
              ))}
            </Stack>
          </Box>

          <FormControlLabel
            control={<Switch checked={link} onChange={e => setLink(e.target.checked)} />}
            label="Keep them in step"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
            {link
              ? alreadyInKit
                ? `The copies join the ${kitName(source.kit_id)} kit, so all of them keep the same required contents.`
                : "Adding, removing or re-quantifying an item in any one of them does the same to the rest. Actual stock stays separate."
              : "The copies start out the same but are then maintained separately. Pick this for containers that only happen to look alike today."}
          </Typography>
          {link && !alreadyInKit && (
            <TextField label="Kit name" size="small" fullWidth value={kit}
                       error={kit.trim() === ""}
                       helperText="What this family of containers is called, e.g. First aid bag"
                       onChange={e => setKit(e.target.value)} />
          )}
          {!link && (
            <Alert severity="info">
              You can link them later — the check looks for any differences first.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" startIcon={<ContentCopyIcon />}
                disabled={!canSave || saving} onClick={save}>
          {saving ? "Copying" : `Make ${values.length} ${values.length === 1 ? "copy" : "copies"}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
